import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Save, ShieldCheck, Check } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AccountLayout from '../../components/AccountLayout';
import { CUSTOMER_TYPE_LABELS, DEFAULT_COUNTRY, getAccountProfile, updateAccountProfile, companyLookup } from '../../config';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  phone: '',
  address: '',
  postal_code: '',
  city: '',
  country: DEFAULT_COUNTRY,
  customer_type: 'particulier',
  company_name: '',
  siret: '',
};

export default function AccountProfile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState({ status: 'idle', name: '' }); // idle | loading | found | notfound

  // Auto-remplissage société par SIRET (API officielle) — permet à un compte Google de
  // devenir pro en saisissant juste son SIRET. Cf gap signalé boss 2026-06-01.
  const runSiretLookup = async (rawSiret) => {
    const digits = (rawSiret || '').replace(/\D/g, '');
    if (digits.length !== 14) { setLookup({ status: 'idle', name: '' }); return; }
    setLookup({ status: 'loading', name: '' });
    try {
      const res = await companyLookup(digits);
      if (res?.found && res.company) {
        const c = res.company;
        setForm((f) => ({
          ...f,
          company_name: c.name || f.company_name,
          address: c.address || f.address,
          postal_code: c.postal_code || f.postal_code,
          city: c.city || f.city,
        }));
        setLookup({ status: 'found', name: c.name || '' });
      } else {
        setLookup({ status: 'notfound', name: '' });
      }
    } catch {
      setLookup({ status: 'notfound', name: '' });
    }
  };

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        const data = await getAccountProfile();
        if (!active) return;
        const currentUser = data.user;
        setForm({
          first_name: currentUser.first_name || '',
          last_name: currentUser.last_name || '',
          phone: currentUser.phone || '',
          address: currentUser.address || '',
          postal_code: currentUser.postal_code || '',
          city: currentUser.city || '',
          country: currentUser.country || DEFAULT_COUNTRY,
          customer_type: currentUser.customer_type || 'particulier',
          company_name: currentUser.company_name || '',
          siret: currentUser.siret || '',
        });
      } catch {
        if (active) setError('Impossible de charger votre profil.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === 'customer_type' && value === 'particulier'
        ? { company_name: '', siret: '' }
        : {}),
    }));
    if (name === 'siret') {
      if (value.replace(/\D/g, '').length === 14) runSiretLookup(value);
      else setLookup({ status: 'idle', name: '' });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      await updateAccountProfile(form);
      await refreshUser();
      setMessage('Profil enregistre.');

      const next = searchParams.get('next');
      if (next) {
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Impossible de mettre a jour votre profil.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AccountLayout title="Mon profil">
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-secondary animate-spin" />
        </div>
      </AccountLayout>
    );
  }

  const showBusinessFields = form.customer_type === 'professionnel';

  return (
    <AccountLayout title={user?.profile_completed ? 'Mon profil' : 'Completer mon profil'}>
      <div className="max-w-4xl space-y-4">
        {!user?.profile_completed && (
          <div className="flex items-start gap-3 bg-secondary/10 border border-secondary/30 rounded-sm p-4 text-sm text-slate-700">
            <ShieldCheck className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
            Completez votre profil pour activer pleinement votre espace client et reserver sans ressaisir vos coordonnees.
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-sm border border-slate-200 p-5 sm:p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Prenom</span>
              <input
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Nom</span>
              <input
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Email Google</span>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full bg-slate-100 border border-slate-200 rounded-sm px-4 py-3 text-sm text-slate-500 cursor-not-allowed"
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Telephone</span>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
          </div>

          <label className="space-y-1.5 block">
            <span className="text-xs text-slate-600">Adresse postale</span>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
              required
            />
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Code postal</span>
              <input
                type="text"
                name="postal_code"
                value={form.postal_code}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
            <label className="space-y-1.5 sm:col-span-2">
              <span className="text-xs text-slate-600">Ville</span>
              <input
                type="text"
                name="city"
                value={form.city}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Pays</span>
              <input
                type="text"
                name="country"
                value={form.country}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                required
              />
            </label>
            <label className="space-y-1.5">
              <span className="text-xs text-slate-600">Type de client</span>
              <select
                name="customer_type"
                value={form.customer_type}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
              >
                {Object.entries(CUSTOMER_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {showBusinessFields && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">Raison sociale</span>
                <input
                  type="text"
                  name="company_name"
                  value={form.company_name}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                  required={showBusinessFields}
                />
              </label>
              <label className="space-y-1.5">
                <span className="text-xs text-slate-600">SIRET</span>
                <input
                  type="text"
                  name="siret"
                  inputMode="numeric"
                  placeholder="14 chiffres — remplit la société"
                  value={form.siret}
                  onChange={handleChange}
                  onBlur={(e) => runSiretLookup(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary outline-none focus:border-secondary"
                  required={showBusinessFields}
                />
                <div className="min-h-[16px] text-xs flex items-center gap-1.5">
                  {lookup.status === 'loading' && <span className="text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Recherche…</span>}
                  {lookup.status === 'found' && <span className="text-emerald-600 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> {lookup.name}</span>}
                  {lookup.status === 'notfound' && <span className="text-amber-600">Introuvable — vérifie le SIRET.</span>}
                </div>
              </label>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 rounded-sm p-3 text-sm text-rose-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {message && <div className="text-sm text-emerald-600">{message}</div>}

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-5 py-2.5 text-sm transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </form>
      </div>
    </AccountLayout>
  );
}
